/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { CalendarUtils } from './calendar-utils';
import dayjs, { Dayjs } from 'dayjs';
import { CommonUtils } from '../../tool/common-utils';
import { Time } from '../../types';

export class CalendarTask {
  id: string;
  label: string;
  color: string;
  icon: string;
  backgroundColor: string;
  borderColor: string;
  dateStart: Dayjs;
  dateEnd: Dayjs;
  enable = true;
  group: { header: string; group: string };

  public position: number;

  constructor(task: {
    id: string;
    label: string;
    dateStart: Date;
    dateEnd?: Date;
    icon?: string;
    backgroundColor?: string;
    borderColor?: string;
    color?: string;
    group?: { header: string; group: string };
  }) {
    this.id = task.id;
    this.label = task.label;
    this.dateStart = dayjs(task.dateStart);
    if (task.dateEnd) {
      this.dateEnd = dayjs(task.dateEnd);
    }
    this.backgroundColor =
      task.backgroundColor ?? CommonUtils.color('primary-color');
    this.borderColor = task.borderColor ?? task.backgroundColor;
    this.color = task.color ? task.color : CommonUtils.color('black');
    this.icon = task.icon;
    this.group = task.group;
  }

  public serialize() {
    return undefined;
  }

  public dayDuration(): number {
    if (
      !this.dateEnd ||
      CalendarUtils.compareDMY(this.dateStart, this.dateEnd)
    ) {
      return 1;
    } else {
      const start = this.dateStart.clone().startOf('d');
      const end = this.dateEnd.clone().startOf('d');
      return end.diff(start, 'day') + 1;
    }
  }

  updateDateStart(day: Dayjs) {
    const duration =
      this.dateEnd.toDate().getTime() - this.dateStart.toDate().getTime();
    this.dateStart = day.clone();
    if (duration > 1) {
      this.dateEnd = day.add(duration, 'ms');
    } else {
      this.dateEnd = day.clone();
    }
  }

  /**
   * Change duration by type
   * @param type
   * 0 update the startDate
   * 1 update the endDate
   * @param duration
   */
  changeDuration(type: 0 | 1, duration: number) {
    const diff = duration - this.dayDuration();
    if (type === 0) {
      this.dateStart = this.dateStart.add(-diff, 'd');
    } else {
      this.dateEnd = this.dateEnd.add(diff, 'd');
    }
  }

  getMinutesForDay(day: Date, start?: Time, end?: Time): number | 'full' {
    if (!this.dateEnd || !CalendarUtils.compareDMY(this.dateEnd, dayjs(day))) {
      return 'full';
    }
    if (this.dateEnd) {
      let duration = this.getDayDuration(day);
      if (start) {
        const mStart = start.hours * 60 + start.minutes;
        const taskStart = CommonUtils.compareDate(this.dateStart.toDate(), day)
          ? this.dateStart.get('h') * 60 + this.dateStart.get('m')
          : 0;
        if (mStart > taskStart) {
          duration -= mStart - taskStart;
        }
      }
      if (end) {
        const mEnd = end.hours * 60 + end.minutes;
        const taskEnd = this.dateEnd.get('h') * 60 + this.dateEnd.get('m');
        if (mEnd < taskEnd) {
          duration -= taskEnd - mEnd;
        }
      }
      return duration;
    }
    return undefined;
  }

  public getDuration(): string {
    if (this.dateEnd) {
      let time = this.dateEnd.diff(this.dateStart, 's');
      time = time - (time % 60);
      return CommonUtils.convertSecondToTimeString(time);
    } else {
      return null;
    }
  }

  around(type: 'start' | 'end', precision: number) {
    const date = type === 'start' ? this.dateStart : this.dateEnd;
    const factor = Math.round(date.minute() / precision);
    date.minute(factor * precision);
  }

  public inDay(d: Date): boolean {
    if (!CommonUtils.compareDate(this.dateStart.toDate(), d)) {
      return (
        this.dateEnd &&
        CommonUtils.diffDMY(this.dateStart.toDate(), d) < 0 &&
        CommonUtils.diffDMY(this.dateEnd.toDate(), d) >= 0
      );
    } else {
      return true;
    }
  }

  private getDayDuration(day: Date): number {
    const endMin = this.dateEnd.get('h') * 60 + this.dateEnd.get('m');
    let startMin = 0;
    if (CommonUtils.compareDate(day, this.dateStart.toDate())) {
      startMin = this.dateStart.get('h') * 60 + this.dateStart.get('m');
    }
    return endMin - startMin;
  }
}
